import { atom, useSetAtom } from 'jotai';
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import Favorites from '@smart-tv/components/favorites';
import FeatureIntro from '@smart-tv/components/feature-intro/feature-intro';
import NpvrFailedRecordingsDialog from '@smart-tv/components/npvr/npvr-failed-recordings-dialog';

import Header from '@smart-tv/components/header/header';
import useQuery from '@smart-tv/hooks/use-query';

import AuthenticatedProviders from './authenticated-providers';
import AuthenticatedRoutes from './authenticated-routes';
import useVodPlayer from './components/player/vod-wrapper/_hooks/use-vod-player';
import StreamPlayer from './components/player/vod-wrapper/stream-player';
import ProgramSheet from './components/program-sheet';
import { FeatureFlags } from '@smart-tv/typings/content';
import type { AvailableFeatureIntros } from '@smart-tv/typings/content';
import getAppFeatureFlags from './utils/kmm/getAppFeatureFlags';
import getActiveFeatureIntros from './utils/kmm/getActiveFeatureIntros';
import closeTvApp from './utils/closeTvApp';
import { IS_WEBOS } from './utils/is-webos';
import useAuthentication from './hooks/use-authentication';
import useNpvrNotifications from '@smart-tv/hooks/use-npvr-notifications';
import { rootStore as npvrRootStore } from '@smart-tv/components/npvr/_stores/store-root';
import { npvrFailedDialogResolvedAtom } from '@smart-tv/atoms';
import { retrievePersistentData } from './utils/persistent-data/persistent-data';

interface QueryParams {
  programsheet?: string;
  player?: string;
}

export const featureFlagsAtom = atom<FeatureFlags | null>(null);
export const activeFeatureIntrosAtom = atom<AvailableFeatureIntros | null>(null);

export const AuthenticatedApp: React.FC = () => {
  const isAutoTestUser = retrievePersistentData<boolean>('isAutoTestUser');
  const { pathname } = useLocation();
  const previousPathnameRef = useRef(pathname);
  const { programsheet } = useQuery<QueryParams>();
  const { isPlaying, handleClose } = useVodPlayer();

  const { closeActiveStreamIfExists } = useAuthentication();
  const { refresh: refreshNpvrNotifications, reset: resetNpvrNotifications } =
    useNpvrNotifications();

  const setFeatureFlags = useSetAtom(featureFlagsAtom);
  const setActiveFeatureIntros = useSetAtom(activeFeatureIntrosAtom);
  const setNpvrFailedDialogResolved = useSetAtom(npvrFailedDialogResolvedAtom);

  const [firstRender, setFirstRender] = React.useState(true);

  // Use ref to always have the latest handleClose in the visibility change handler without needing to re-subscribe to the event on every render
  const handleCloseRef = useRef(handleClose);
  handleCloseRef.current = handleClose;

  const refreshNpvrNotificationsRef = useRef(refreshNpvrNotifications);
  refreshNpvrNotificationsRef.current = refreshNpvrNotifications;

  const resetNpvrNotificationsRef = useRef(resetNpvrNotifications);
  resetNpvrNotificationsRef.current = resetNpvrNotifications;

  React.useEffect(() => {
    (async () => {
      const data = await getAppFeatureFlags();
      setFeatureFlags(data);

      const intros = await getActiveFeatureIntros();
      setActiveFeatureIntros(intros);
    })();
  }, [setFeatureFlags, setActiveFeatureIntros]);

  // Initial check so notification dots are correct as soon as the app starts; reset on
  // unmount (logout/account switch) so the next session doesn't inherit stale state.
  React.useEffect(() => {
    let active = true;
    refreshNpvrNotificationsRef.current().then((success) => {
      // Don't block FeatureIntro when the failed recordings check can't be made.
      if (active && !success) setNpvrFailedDialogResolved(true);
    });
    return () => {
      active = false;
      resetNpvrNotificationsRef.current();
      npvrRootStore.npvrStore.reset();
    };
  }, [setNpvrFailedDialogResolved]);

  useEffect(() => {
    if (pathname === '/' && previousPathnameRef.current !== pathname) {
      refreshNpvrNotificationsRef.current();
    }
    previousPathnameRef.current = pathname;
  }, [pathname]);

  React.useEffect(() => {
    if (firstRender) {
      setFirstRender(false);
      closeActiveStreamIfExists();
      return;
    }
    // eslint-disable-next-line
  }, [closeActiveStreamIfExists]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if ((window.tizen && window.tizen.application) || IS_WEBOS) {
          handleCloseRef.current?.();

          // Exit immediately - don't wait for async cleanup
          closeTvApp();
        }
      } else {
        // 17E: refresh notification state when the app comes back to the foreground.
        refreshNpvrNotificationsRef.current();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <AuthenticatedProviders>
      {!isPlaying && <Header />}
      <div className="framework-container">
        <AuthenticatedRoutes />
      </div>
      <StreamPlayer />
      {programsheet && <ProgramSheet />}
      <Favorites />
      <NpvrFailedRecordingsDialog />
      {!isAutoTestUser && <FeatureIntro />}
    </AuthenticatedProviders>
  );
};

export default AuthenticatedApp;
